import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { httpClient } from '../network/HttpClient';

type AppAuthUser = {
    sub?: string;
    name?: string;
    email?: string;
    picture?: string;
};

type AppAuthContextType = {
    isLoading: boolean;
    isAuthenticated: boolean;
    user: AppAuthUser | null;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    getAccessToken: () => Promise<string>;
};

type DevLoginResponse = {
    token: string;
    user?: AppAuthUser;
};

const DEV_AUTH_TOKEN_KEY = 'dev_auth_token';
const DEV_AUTH_USER_KEY = 'dev_auth_user';

const AppAuthContext = createContext<AppAuthContextType | undefined>(undefined);

function useAppAuthContext(): AppAuthContextType {
    const ctx = useContext(AppAuthContext);
    if (!ctx) {
        throw new Error('useAppAuth must be used within AppAuthProvider');
    }
    return ctx;
}

const Auth0BridgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const {
        isLoading,
        isAuthenticated,
        user,
        loginWithRedirect,
        logout,
        getAccessTokenSilently,
    } = useAuth0();

    const value = useMemo<AppAuthContextType>(() => ({
        isLoading,
        isAuthenticated,
        user: user
            ? {
                sub: user.sub,
                name: user.name,
                email: user.email,
                picture: user.picture,
            }
            : null,
        login: async () => {
            await loginWithRedirect();
        },
        logout: async () => {
            logout({ logoutParams: { returnTo: window.location.origin } });
        },
        getAccessToken: async () => getAccessTokenSilently(),
    }), [
        isLoading,
        isAuthenticated,
        user,
        loginWithRedirect,
        logout,
        getAccessTokenSilently,
    ]);

    return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
};

const DevAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<AppAuthUser | null>(null);

    useEffect(() => {
        const storedToken = localStorage.getItem(DEV_AUTH_TOKEN_KEY);
        const storedUser = localStorage.getItem(DEV_AUTH_USER_KEY);

        // If token is missing or empty, clear state
        if (!storedToken) {
            setToken(null);
            setUser(null);
            setIsLoading(false);
            return;
        }

        setToken(storedToken);

        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch {
                setUser(null);
            }
        }

        setIsLoading(false);
    }, []);

    const value = useMemo<AppAuthContextType>(() => ({
        isLoading,
        isAuthenticated: !!token,
        user,
        login: async () => {
            const response: DevLoginResponse = await httpClient.devLogin();
            localStorage.setItem(DEV_AUTH_TOKEN_KEY, response.token);
            if (response.user) {
                localStorage.setItem(DEV_AUTH_USER_KEY, JSON.stringify(response.user));
            } else {
                localStorage.removeItem(DEV_AUTH_USER_KEY);
            }
            setToken(response.token);
            setUser(response.user ?? null);
        },
        logout: async () => {
            localStorage.removeItem(DEV_AUTH_TOKEN_KEY);
            localStorage.removeItem(DEV_AUTH_USER_KEY);
            setToken(null);
            setUser(null);
        },
        getAccessToken: async () => {
            // Always read from localStorage for robustness
            const latestToken = localStorage.getItem(DEV_AUTH_TOKEN_KEY);
            if (!latestToken) {
                setToken(null);
                setUser(null);
                throw new Error('[AppAuth] No dev token found. Please log in.');
            }
            return latestToken;
        },
    }), [isLoading, token, user]);

    return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
};

export const AppAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const authMode = process.env.REACT_APP_AUTH_MODE?.trim();
    const isDevBypass = authMode === 'dev-bypass';

    if (isDevBypass) {
        return <DevAuthProvider>{children}</DevAuthProvider>;
    }

    if (!process.env.REACT_APP_AUTH0_DOMAIN || !process.env.REACT_APP_AUTH0_CLIENT_ID) {
        throw new Error('Missing Auth0 configuration in environment variables.');
    }

    return (
        <Auth0Provider
            domain={process.env.REACT_APP_AUTH0_DOMAIN}
            clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
            useRefreshTokens={true}
            cacheLocation="localstorage"
            authorizationParams={{
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email offline_access',
                redirect_uri: window.location.origin
            }}
        >
            <Auth0BridgeProvider>{children}</Auth0BridgeProvider>
        </Auth0Provider>
    );
};

export const useAppAuth = () => useAppAuthContext();
